import API from '../../../../services/rest/api';
import type { AssignBody, Food, Id, Plan, SlotKey, Template } from '../types';
import {
  foodFromApiItem,
  mapApiTemplateDetail,
  mapApiTemplateListItem,
  planToCreatePayload,
  planToTemplatePayload,
} from '../lib/plan';

const n = (v: any) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

const hydrateRow = async (row: any): Promise<Food> => {
  const base: Food = {
    id: row.FOOD_ID,
    name: row.FOOD_NAME || row.food_name || '',
    qty: 1,
    unit: row.UNIT_NAME || '',
    per: [0, 0, 0, 0],
    measureId: row.UNIT_ID,
  };
  try {
    const response = await API.GetFoodItemNutritions({
      food_id: row.FOOD_ID,
      unit_id: row.UNIT_ID,
    });
    if (Number(response?.code) === 1 && response?.data) {
      const d = response.data;
      return {
        ...base,
        per: [n(d.calories), n(d.protein), n(d.carbs), n(d.fats ?? d.fat)],
      };
    }
  } catch {
    // Search still works without macros; option editor will show zeros.
  }
  return foodFromApiItem({ ...row, quantity: 1, calories: row.Energy_kcal });
};

export interface DietPlanApi {
  searchFoods: (q: string, slot: SlotKey, signal: AbortSignal) => Promise<Food[]>;
  listTemplates: () => Promise<Template[]>;
  getTemplate: (id: Id) => Promise<Template>;
  listPlans: (patientId: string) => Promise<any[]>;
  savePlan: (plan: Plan, status: string, patientId: string) => Promise<any>;
  saveTemplate: (plan: Plan, save: NonNullable<AssignBody['saveTemplate']>) => Promise<any>;
}

const TEMPLATE_LIST_PAGE_SIZE = 50;
/** Guard against a bad total_records value looping forever. */
const TEMPLATE_LIST_MAX_PAGES = 200;

async function fetchAllTemplates(): Promise<Template[]> {
  const mapped: Template[] = [];
  let page = 1;

  while (page <= TEMPLATE_LIST_MAX_PAGES) {
    const response = await API.GetDietTemplateList({
      page,
      limit: TEMPLATE_LIST_PAGE_SIZE,
    });
    if (Number(response?.code) !== 1) {
      if (page === 1) return [];
      break;
    }
    const rows: unknown[] = Array.isArray(response?.data) ? response.data : [];
    if (!rows.length) break;
    mapped.push(...rows.map((row) => mapApiTemplateListItem(row as any)));

    const total = Number(response?.total_records);
    if (rows.length < TEMPLATE_LIST_PAGE_SIZE) break;
    if (Number.isFinite(total) && total > 0 && mapped.length >= total) break;
    page += 1;
  }

  return mapped;
}

export const dietPlanApi: DietPlanApi = {
  async searchFoods(q, _slot, signal) {
    const query = q.trim();
    if (!query) return [];
    const data = await API.GetFoodItemsOptions({ page: 1, food_name: query });
    if (signal.aborted) return [];
    if (Number(data?.code) !== 1) return [];
    const rows = (data?.data || []).slice(0, 15);
    return Promise.all(rows.map(hydrateRow));
  },

  async listTemplates() {
    return fetchAllTemplates();
  },

  async getTemplate(id) {
    const response = await API.GetDietTemplateByID(id);
    if (Number(response?.code) !== 1) throw new Error(response?.msg || 'Failed to load template');
    return mapApiTemplateDetail(response.data || {});
  },

  async listPlans(patientId) {
    const response = await API.GetDietPlan({ diet_type: 'all', patient_id: patientId });
    if (Number(response?.code) !== 1) {
      throw new Error(response?.message || response?.msg || 'Could not load existing diet plans');
    }
    return Array.isArray(response?.data) ? response.data : [];
  },

  async savePlan(plan, status, patientId) {
    const payload = planToCreatePayload(plan, {
      patientId,
      status,
      dietPlanId: plan.id ? String(plan.id) : undefined,
    });
    return API.CreateDietPlanNew(payload);
  },

  async saveTemplate(plan, save) {
    const name =
      save.kind === 'mine' ? save.name : (plan.templateName || plan.name);
    const payload = planToTemplatePayload(plan, name);
    if (save.kind === 'master') {
      return API.UpdateTemplateList(save.templateId, payload);
    }
    return API.CreateDietTemplate(payload);
  },
};
